import * as React from 'react';
import { createStyles, ActionIcon, Group, Avatar, Text, Box, Menu } from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import gravatar from 'gravatar';

// Contexts
import { AuthContext } from '../../contexts/Auth';

// Hooks
import { usePermission } from '../../hooks/usePermission';

// Types
import { IUserRole } from '../../types';

const useStyles = createStyles((theme) => ({
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%'
    },
    rightCont: {
        gap: '16px',
        display: 'flex',
        flexDirection: 'row'
    },
    logo: {
        height: '100%',
        maxHeight: '30px',
        marginLeft: '8px'
    },
    credentialsContainer: {
        cursor: 'pointer',
        width: '100%',
        gap: '8px'
    },
    listItem: {
        width: '100%',
        display: 'block',
        padding: '0.5rem'
    },
    userDetail: {
        maxWidth: '8.5rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    nameBox: {
        borderBottom: '1px solid #dee2e6',
        padding: '0.5rem'
    }
}));

export const UserWidget: React.FC = () => {
    const { authState } = React.useContext(AuthContext);
    const { classes } = useStyles();
    const { t } = useTranslation();
    const isCicUser = usePermission([IUserRole.CIC_ADMIN, IUserRole.CIC_OWNER, IUserRole.CIC_USER]);
    const gravatarSrc = React.useMemo(() => {
        return authState?.email ? gravatar.url(authState.email) : undefined;
    }, [authState]);

    return authState ? (
        <Menu shadow="md" trigger="hover" width={200} closeDelay={1000}>
            <Menu.Target>
                <Group position="apart" className={classes.credentialsContainer}>
                    <Group>
                        <Avatar src={`${gravatarSrc}?d=mp`} alt={authState?.email} radius="xl" size={25} />
                        <Box>
                            <Text weight={500} size="sm" sx={{ lineHeight: 1.3 }} className={classes.userDetail}>
                                {authState.name}
                            </Text>
                            <Text size="xs" c="dimmed" sx={{ lineHeight: 1.3 }} className={classes.userDetail}>
                                {authState.email}
                            </Text>
                        </Box>
                    </Group>
                    <ActionIcon variant="transparent">
                        <IconLogout size={14} />
                    </ActionIcon>
                </Group>
            </Menu.Target>
            <Menu.Dropdown>
                <Box className={classes.nameBox}>
                    <Text weight={500} size="sm" sx={{ lineHeight: 1.3 }} className={classes.userDetail}>
                        {authState.name}
                    </Text>
                    <Text size="xs" c="dimmed" sx={{ lineHeight: 1.3 }} className={classes.userDetail}>
                        {authState.email}
                    </Text>
                </Box>
                <NavLink className={classes.listItem} to={`/${isCicUser ? 'admin' : 'client'}/settings`}>
                    {t('shared.settings.title', { ns: 'routes' })}
                </NavLink>
                <NavLink className={classes.listItem} to="/logout">
                    {t('logout', { ns: 'common' })}
                </NavLink>
            </Menu.Dropdown>
        </Menu>
    ) : null;
};
